import React from 'react';
import { FormProvider, useFieldArray } from 'react-hook-form';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import Button from '@mui/material/Button';
import useCustomFields from '@/lib/hooks/swr/useCustomFields';
import translate from '@/lib/intl/translate';
import useConstituentSearchAdvancedFilter, {
  ADVANCED_FILTERS,
  DONATION_AGG,
} from '../hooks/useConstituentSearchAdvancedFilter';
import isDonationField from '../utils/isDonationField';
import mapAdvancedFormDataToFilters from '../utils/mapAdvancedFormDataToFilters';
// eslint-disable-next-line import/no-cycle -- TODO: Fix this the next time the file is edited.
import AdvancedFilterFieldset from './AdvancedFilterFieldset';
import AggregateSwitch from './AggregateSwitch';

type ConstituentSearchAdvancedFilterProps = {
  toggleAdvancedFilters: () => void;
  onClose: () => void;
};

const ConstituentSearchAdvancedFilter = React.forwardRef<
  HTMLFormElement,
  ConstituentSearchAdvancedFilterProps
>(({ toggleAdvancedFilters, onClose }, ref) => {
  const { clearQuery, formMethods, updateQuery } =
    useConstituentSearchAdvancedFilter();

  const { control, handleSubmit, getValues, reset, watch } = formMethods;

  const { data: customFields } = useCustomFields();
  const onSubmit = React.useCallback(
    (e: React.FormEvent) =>
      handleSubmit(async (formData) => {
        updateQuery({
          donationAgg: formData[DONATION_AGG],
          ...mapAdvancedFormDataToFilters(formData, customFields),
        });
        onClose();
      })(e),
    [customFields, handleSubmit],
  );

  const { fields, append, remove } = useFieldArray({
    control,
    name: ADVANCED_FILTERS,
    rules: { minLength: 1 },
  });

  React.useEffect(() => {
    if (!fields.length) {
      append({
        field: '',
        value: '',
      });
    }
  }, [fields]);

  const showAggToggle =
    watch(ADVANCED_FILTERS).filter(
      ({ field }) => field && isDonationField(field),
    ).length > 1;

  return (
    <FormProvider {...formMethods}>
      <form
        onSubmit={onSubmit}
        className="flex grow flex-col"
        aria-label={translate('ADVANCED_FILTERS')}
        ref={ref}
      >
        <div className="flex grow flex-col p-6">
          <div className="grid grid-cols-1 gap-3">
            <div className="mb-3">
              <Button
                onClick={toggleAdvancedFilters}
                className="p-0"
                startIcon={<ChevronLeft />}
                classes={{
                  startIcon: 'mr-0',
                }}
              >
                {translate('BACK_TO_FILTERS')}
              </Button>
            </div>
            <div className="type-body2">{translate('ADVANCED_FILTER')}</div>
            {fields.map((field, index) => (
              <React.Fragment key={field.id}>
                <AdvancedFilterFieldset
                  onDuplicate={() =>
                    append({ ...getValues(`${ADVANCED_FILTERS}.${index}`) })
                  }
                  onRemove={() => remove(index)}
                  fieldArrayName={ADVANCED_FILTERS}
                  index={index}
                />
                <div>
                  <Button
                    color="secondary"
                    variant="contained"
                    size="small"
                    disabled={!(index === fields.length - 1)}
                    onClick={() =>
                      append({
                        field: '',
                        value: '',
                      })
                    }
                  >
                    {translate('AND')}
                  </Button>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
        {showAggToggle && (
          <div className="mx-6 mt-auto border-t border-grey-300 py-3">
            <AggregateSwitch />
          </div>
        )}
        <div className="flex-0 sticky bottom-0 flex flex-row-reverse border-t border-grey-300 bg-white px-6 pt-4 pb-6">
          <Button variant="contained" type="submit">
            {translate('APPLY')}
          </Button>
          <Button
            onClick={() => {
              reset();
              clearQuery();
              onClose();
            }}
          >
            {translate('CLEAR_ALL')}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
});

export default ConstituentSearchAdvancedFilter;
